/**
 * Control Room: opprette, fryse og åpne en organisasjon, og abonnementet (sak 238, trinn 4;
 * migrering 0480).
 *
 * `/v1/platform/organization-invitations` (liste, lag, trekk tilbake) og
 * `/v1/platform/organizations/:id/admin|frozen|subscription`. Bare for plattformadminer; alle andre
 * får 404.
 *
 * «NYTT HUS»-INVITASJONEN er den eneste veien til en ny organisasjon. Den har typen (festival eller
 * byrå), og den som løser den inn blir første superadmin. Tokenet finnes bare i svaret på `POST`,
 * én gang — basen holder bare hashen.
 *
 * `organizationInvitationPreviewSchema` er den offentlige forhåndsvisningen
 * (`GET /v1/organizations/invitation-preview?token=`), som `/bli-med/$token` viser.
 */
import { z } from 'zod';

import { organizationModuleListSchema, organizationModuleSchema } from './control-room-modules.js';
import { emailDeliveryOutcomeSchema, messageDeliverySchema } from './messaging.js';

/** Typene en ny organisasjon kan få fra Control Room. Personlig og plattform lages aldri her. */
export const CONTROL_ROOM_INVITATION_KINDS = ['festival', 'agency'] as const;
export type ControlRoomInvitationKind = (typeof CONTROL_ROOM_INVITATION_KINDS)[number];
export const controlRoomInvitationKindSchema = z
  .enum(CONTROL_ROOM_INVITATION_KINDS)
  .meta({ id: 'ControlRoomInvitationKind' });

/** Abonnementets status. Null er «ikke satt». */
export const CONTROL_ROOM_SUBSCRIPTION_STATUSES = ['beta', 'paid'] as const;
export type ControlRoomSubscriptionStatus = (typeof CONTROL_ROOM_SUBSCRIPTION_STATUSES)[number];
export const controlRoomSubscriptionStatusSchema = z
  .enum(CONTROL_ROOM_SUBSCRIPTION_STATUSES)
  .meta({ id: 'ControlRoomSubscriptionStatus' });

export const controlRoomOrganizationInvitationSchema = z
  .object({
    id: z.uuid(),
    email: z.string(),
    organizationName: z.string().nullable(),
    kind: controlRoomInvitationKindSchema,
    /** Modulene organisasjonen får når invitasjonen løses inn (0483). Typen over er utledet av dem. */
    modules: z.array(organizationModuleSchema),
    note: z.string().nullable(),
    expiresAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
    /** Utløpt, men ikke trukket tilbake: lenken virker ikke lenger. */
    expired: z.boolean(),
    invitedByEmail: z.string().nullable(),
    /** Jobb 2 (0491): e-posten med lenken. */
    delivery: messageDeliverySchema.nullable(),
    /** Byrået i den offisielle lista som kobles ved innløsningen (0940). */
    officialAgencyName: z.string().nullable(),
  })
  .meta({ id: 'ControlRoomOrganizationInvitation' });

/** Ventende: ikke løst inn og ikke trukket tilbake. Høyst 200, nyeste først. */
export const controlRoomOrganizationInvitationListResponseSchema = z
  .object({ items: z.array(controlRoomOrganizationInvitationSchema) })
  .meta({ id: 'ControlRoomOrganizationInvitationListResponse' });

export const createControlRoomOrganizationInvitationRequestSchema = z
  .object({
    organizationName: z.string().trim().min(2).max(200),
    /** Fem avkryssingsbokser i stedet for type (0483). Minst én; typen utledes av dem. */
    modules: organizationModuleListSchema,
    email: z.email().max(320),
    note: z.string().trim().max(1000).optional(),
    /**
     * Byrået i den offisielle lista som organisasjonen ER (0940, 253.68). Krever modulen Byrå. Kobles
     * når invitasjonen løses inn.
     */
    officialAgencyId: z.uuid().nullish(),
  })
  .meta({ id: 'CreateControlRoomOrganizationInvitationRequest' });

export const createControlRoomOrganizationInvitationResponseSchema = z
  .object({
    invitation: controlRoomOrganizationInvitationSchema,
    /** Det rå tokenet, én gang. Lenken er `/bli-med/<token>`. */
    token: z.string(),
    /** En åpen invitasjon til samme adresse som ble trukket tilbake av denne. */
    replacedId: z.uuid().nullable(),
    /** E-posten med lenken: lagt i køen, eller stoppet av mottakerlista (sak 7). */
    emailDelivery: emailDeliveryOutcomeSchema,
  })
  .meta({ id: 'CreateControlRoomOrganizationInvitationResponse' });

export const controlRoomOrganizationAdminSchema = z
  .object({
    organizationId: z.uuid(),
    name: z.string(),
    kind: z.enum(['festival', 'agency', 'personal', 'platform']),
    status: z.enum(['trial', 'active', 'suspended']),
    frozen: z.boolean(),
    frozenAt: z.iso.datetime().nullable(),
    frozenReason: z.string().nullable(),
    /** 245.6: beskjeden til kunden — det eneste medlemmene ser. Grunnen er plattformens egen. */
    frozenMessage: z.string().nullable(),
    frozenByEmail: z.string().nullable(),
    subscription: z.object({
      plan: z.string().nullable(),
      status: controlRoomSubscriptionStatusSchema.nullable(),
      note: z.string().nullable(),
      updatedAt: z.iso.datetime().nullable(),
    }),
  })
  .meta({ id: 'ControlRoomOrganizationAdmin' });

/**
 * Frys med en grunn, eller åpne. Grunnen er påkrevd ved frysing (detaljkode `reason_required`) og
 * står bare i Control Room. `message` er beskjeden til kunden (245.6): på «fryst»-siden, i varselet
 * og i e-posten til superadminene. Valgfri.
 */
export const setControlRoomOrganizationFrozenRequestSchema = z
  .object({
    frozen: z.boolean(),
    reason: z.string().trim().max(1000).optional(),
    message: z.string().trim().max(2000).optional(),
  })
  .meta({ id: 'SetControlRoomOrganizationFrozenRequest' });

export const updateControlRoomSubscriptionRequestSchema = z
  .object({
    plan: z.string().trim().max(100).nullable(),
    status: controlRoomSubscriptionStatusSchema.nullable(),
    note: z.string().trim().max(2000).nullable(),
  })
  .meta({ id: 'UpdateControlRoomSubscriptionRequest' });

/** Det den som åpner en «nytt hus»-lenke ser før innlogging. */
export const organizationInvitationPreviewSchema = z
  .object({
    email: z.string(),
    organizationName: z.string().nullable(),
    kind: controlRoomInvitationKindSchema,
    expiresAt: z.iso.datetime(),
    open: z.boolean(),
    hasAccount: z.boolean(),
    /** Kontoen finnes, men uten fornavn: siden spør om navn og telefon likevel (1327). */
    needsProfile: z.boolean(),
    /** QA15-S K3 (1263): modulene invitasjonen gir, så siden kan si «spillested» og ikke den lagrede typen. */
    modules: z.array(organizationModuleSchema).default([]),
  })
  .meta({ id: 'OrganizationInvitationPreview' });

export const organizationInvitationPreviewQuerySchema = z
  .object({ token: z.string().trim().min(20).max(200) })
  .meta({ id: 'OrganizationInvitationPreviewQuery' });

/** En organisasjon personen er medlem av og som er fryst (`/v1/me`). */
export const frozenOrganizationSchema = z
  .object({
    id: z.uuid(),
    name: z.string(),
    /*
     * 245.6: beskjeden plattformen ga kunden ved frysingen (ikke den interne grunnen, som F59 viste),
     * og når. Valgfri, så eldre klienter tåler svaret.
     */
    message: z.string().nullable().optional(),
    frozenAt: z.iso.datetime().nullable().optional(),
  })
  .meta({ id: 'FrozenOrganization' });

export type ControlRoomOrganizationInvitation = z.infer<
  typeof controlRoomOrganizationInvitationSchema
>;
export type ControlRoomOrganizationInvitationListResponse = z.infer<
  typeof controlRoomOrganizationInvitationListResponseSchema
>;
export type CreateControlRoomOrganizationInvitationRequest = z.infer<
  typeof createControlRoomOrganizationInvitationRequestSchema
>;
export type CreateControlRoomOrganizationInvitationResponse = z.infer<
  typeof createControlRoomOrganizationInvitationResponseSchema
>;
export type ControlRoomOrganizationAdmin = z.infer<typeof controlRoomOrganizationAdminSchema>;
export type SetControlRoomOrganizationFrozenRequest = z.infer<
  typeof setControlRoomOrganizationFrozenRequestSchema
>;
export type UpdateControlRoomSubscriptionRequest = z.infer<
  typeof updateControlRoomSubscriptionRequestSchema
>;
export type OrganizationInvitationPreview = z.infer<typeof organizationInvitationPreviewSchema>;
export type OrganizationInvitationPreviewQuery = z.infer<
  typeof organizationInvitationPreviewQuerySchema
>;
export type FrozenOrganization = z.infer<typeof frozenOrganizationSchema>;
